import { useEffect, useState } from "react";
import {
  ApiError,
  downloadExport,
  editPlan,
  exportArtefact,
  issuePlan,
  readIct,
  readPlan,
  recordIctGate,
  type AuditPlan as Plan,
  type IctStanding,
  type PlanDay,
  type PlanState,
} from "../api/client";
import { Callout } from "../components/Callout";
import { TextAreaField } from "../components/TextAreaField";
import { PlanBlocked } from "./ApprovalCard";
import { IctGatesCard, PLAN_ISSUE_GATES } from "./IctGates";
import type { StepKey } from "./steps";
import { idempotencyKey } from "../api/idempotency";

/**
 * Screens 39 to 42 — the audit plan, its edit dialog, and the issue.
 *
 * **Every figure on it is generated.** The agenda is derived from the resolved clause queue
 * and the agreed Stage 1 hours, then laid onto audit days: the clock restarts at 09:00 each
 * day, breaks fall after four counted hours and are marked not counted (Cl. 2.1.3), and no
 * row reads past the end of its day. The reference plan this replaces carried rows reading
 * "11:30 pm – 2:30 pm" and a footer reading "+7.9 h unallocated"; the total here is the
 * agreed duration, exactly, and it says so.
 *
 * **The lead auditor may change the words and not the arithmetic.** Objectives, scope,
 * criteria, exclusions and the agenda note are editable and every edit is flagged with the
 * Complao draft retained beside it. The agenda's hours are not, because an agenda edited by
 * hand no longer sums to the duration both parties approved — where different time is
 * wanted, the route is a duration adjustment on Step 2, which is reasoned and recorded.
 *
 * **Rows carrying a remote-blocked checkpoint say so here**, on the plan, before the
 * session. Cl. 4.5 limits remote technique for OH&SMS to reviewing documents and records
 * and interviewing staff, and an auditor who discovers that at sign-off has already run the
 * session that cannot be signed off.
 *
 * **The MD 4 competency attestation is recorded here**, for the same reason Cl. 4.2.2 and
 * Cl. 4.2.7 are recorded on Step 2: the gate refuses this step, so the answer to it belongs
 * on this screen. Cl. 4.2.4 sits at ``planning`` because the plan is what names the
 * technology in use — attested any earlier, it would attest to nothing — and the plan above
 * is the document the lead auditor is attesting against when they record it.
 */
export function AuditPlanScreen({
  auditId,
  onChanged,
  onStep,
  onIssued,
}: {
  auditId: string;
  /** Issuing the plan moves the audit to `awaiting_documents` — the shell's rail reads it. */
  onChanged: () => void;
  onStep: (step: StepKey) => void;
  /**
   * The plan is issued, and the reader goes with it to Steps 4 and 5.
   *
   * **The link is not handed up, because it no longer has to be.** Issuing is the last act
   * of Step 3 — the screen it leaves behind has no editable field on it and nothing further
   * to do — and the link is a fact about the auditee's access, which is what Steps 4 and 5
   * are. Readiness reads it from the server for as long as it works, so this is a move and
   * nothing else: a token passed alongside would be a second copy of a fact one screen can
   * already ask for, and the two would disagree the first time it was re-issued.
   */
  onIssued: () => void;
}) {
  const [state, setState] = useState<PlanState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<EditTarget | null>(null);
  const [issuing, setIssuing] = useState(false);
  const [ict, setIct] = useState<IctStanding | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exported, setExported] = useState<string | null>(null);

  useEffect(() => {
    readPlan(auditId)
      .then(setState)
      .catch((cause: unknown) => setError(problem(cause, "The plan")));
    readIct(auditId)
      .then(setIct)
      .catch(() => undefined);
  }, [auditId]);

  if (error && !state) {
    return (
      <p className="problem" role="alert">
        {error}
      </p>
    );
  }
  if (!state) return <p className="sub">Reading the plan&hellip;</p>;

  if (state.status === "blocked" || !state.plan) {
    return (
      <PlanBlocked
        awaiting={state.awaiting ?? []}
        reason={state.blocked_reason ?? null}
        onBack={() => onStep("determination")}
      />
    );
  }

  const plan = state.plan;
  const draft = plan.status === "draft";
  const edits = plan.edits ?? [];
  const edited = new Set(edits.map((entry) => entry.field));

  const save = async (field: string, value: string) => {
    try {
      setState(await editPlan(auditId, field, value));
      setEditing(null);
      setError(null);
    } catch (cause) {
      setError(problem(cause, "The edit"));
    }
  };

  const recordGate = async (gate: string, satisfied: boolean) => {
    try {
      setIct(await recordIctGate(auditId, gate, satisfied));
      setError(null);
    } catch (cause) {
      setError(problem(cause, "The gate"));
    }
  };

  /**
   * The plan as a PDF, taken from Step 3 rather than only from the report screen.
   *
   * The export is a **recorded** act, not a client-side print: the server renders it, stores
   * the bytes content-addressed and writes a `document_exports` row saying who took a copy
   * and when, and the record hash it prints in the footer ties the paper back to the chain.
   * So the bytes are fetched — an `<a href>` would not carry the bearer token — and the
   * confirmation names the hash, because that is what makes a printed copy checkable.
   *
   * **A draft exports watermarked and unbranded**, and the button says so. The lead auditor
   * needs to read the plan away from the screen before approving it, which is most of what
   * this button is for; what they must not be able to do is put a copy in circulation that
   * is indistinguishable from the one the auditee is issued.
   *
   * **`format: "docx"` is the ADR 0028 exception to PDF-only** — available at either state,
   * the same as the PDF: watermarked and unbranded while a draft, the certification body's
   * own mark once approved. Amended 2026-09-15 to cover the approved plan too, a named
   * departure from the reasoning ADR 0011 gives for every other issued artefact — the ADR
   * records what that knowingly accepts.
   */
  const takeExport = async (format: "pdf" | "docx") => {
    setExporting(true);
    setError(null);
    try {
      const result = await exportArtefact(auditId, "plan", format);
      const blob = await downloadExport(auditId, result.export_id);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `plan-${plan.reference}.${format}`;
      anchor.click();
      URL.revokeObjectURL(url);
      setExported(
        `Plan exported${result.branded ? "" : " as a watermarked draft"}${
          format === "docx" ? ", as Word" : ""
        }. Record ${result.record_hash.slice(0, 16)}… is printed in the footer of every page.`,
      );
    } catch (cause) {
      setError(problem(cause, "The export"));
    } finally {
      setExporting(false);
    }
  };

  /**
   * Approve and issue — and then leave.
   *
   * The state is set before the move so that a reader who walks straight back to Step 3
   * reads the approved plan rather than the draft they just replaced. `onIssued` then
   * carries the reader to Steps 4 and 5, where the access it grants is the subject of the
   * screen and where the link itself is read.
   */
  const issue = async () => {
    setError(null);
    try {
      const issued = await issuePlan(auditId, idempotencyKey());
      setState({ audit_id: auditId, status: "approved", plan: issued.plan, awaiting: [] });
      setIssuing(false);
      onChanged();
      onIssued();
    } catch (cause) {
      setError(problem(cause, "The plan"));
    }
  };

  return (
    <>
      <div className="between" style={{ marginBottom: "14px" }}>
        <div>
          <div className="eyebrow">Step 3 · Lead auditor</div>
          <h1>Audit plan — {plan.client_organisation}</h1>
          <p className="sub">
            <span className="mono">{plan.reference}</span> · generated from the confirmed
            scope split and agreed duration
          </p>
        </div>
        <div style={{ textAlign: "right" }}>
          <span className={draft ? "pill p-wait" : "pill p-ok"}>
            {draft ? "Draft — awaiting your approval" : "Approved and issued"}
          </span>
          <div className="row" style={{ marginTop: "8px", gap: "6px", justifyContent: "flex-end" }}>
            <button
              type="button"
              className="btn sec sm"
              disabled={exporting}
              onClick={() => void takeExport("pdf")}
            >
              {exporting
                ? "Exporting…"
                : draft
                  ? "Export watermarked draft"
                  : "Export plan PDF"}
            </button>
            <button
              type="button"
              className="btn sec sm"
              disabled={exporting}
              onClick={() => void takeExport("docx")}
            >
              {exporting ? "Exporting…" : "Export as Word"}
            </button>
          </div>
        </div>
      </div>

      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}

      {exported ? (
        <Callout tone="info" title="Export recorded.">
          {exported}
        </Callout>
      ) : null}

      {draft ? (
        <Callout tone="warn" title="Every element is editable before approval.">
          Edited fields are flagged as manually adjusted and the original Complao draft is
          retained alongside your approved version. Nothing is issued to the auditee until
          you confirm.
        </Callout>
      ) : null}

      <div className="card">
        <h2>Objectives, scope and criteria</h2>
        {(
          [
            ["objectives", "Audit objectives"],
            ["scope", "Audit scope"],
            ["criteria", "Audit criteria"],
            ["exclusions", "Exclusions"],
          ] as const
        ).map(([field, label]) => (
          <NarrativeField
            key={field}
            field={field}
            label={label}
            value={plan.narrative[field]}
            generated={plan.generated[field]}
            edited={edited.has(field)}
            editedBy={edits.find((entry) => entry.field === field) ?? null}
            editable={draft}
            onEdit={() =>
              setEditing({ field, label, value: plan.narrative[field], generated: plan.generated[field] })
            }
          />
        ))}
      </div>

      <div className="card">
        <h2>Team, method and duration</h2>
        <dl className="kv">
          <dt>Lead auditor</dt>
          <dd>{plan.lead_auditor ?? "—"}</dd>
          <dt>Conducted by</dt>
          <dd>{plan.conducted_by}</dd>
          <dt>Technical expert</dt>
          <dd>{plan.technical_expert ?? "Not required"}</dd>
          <dt>Delivery method</dt>
          <dd>{plan.delivery_method}</dd>
          <dt>Agreed duration</dt>
          <dd>
            <span className="mono">{plan.agreed_stage1_hours} hours</span> Stage 1 —{" "}
            {plan.agreed_percent_of_calculated}% of the calculated Stage 1 allocation ·{" "}
            {plan.agreed_days} days total{" "}
            {plan.below_floor ? <span className="pill p-red">Below threshold</span> : null}
          </dd>
          <dt>Audit date</dt>
          <dd>{plan.planned_date}</dd>
        </dl>
      </div>

      <Agenda plan={plan} edited={edited.has("agenda_note")} editable={draft}
        onEdit={() =>
          setEditing({
            field: "agenda_note",
            label: "Agenda sequencing and time allocation",
            value: plan.narrative.agenda_note,
            generated: plan.generated.agenda_note,
          })
        }
      />

      <Documents plan={plan} />

      <div className="card">
        <h2>Confidentiality and impartiality</h2>
        <p style={{ fontSize: "13px" }}>
          Conducted under the confidentiality provisions of ISO/IEC 17021-1 Cl. 4.6.
          Management system consultancy declared:{" "}
          <b>{plan.consultancy_declared ? "Yes" : "No"}</b>. The lead auditor retains full
          responsibility for all audit outcomes and for report sign-off prior to issuance.
        </p>
      </div>

      {/*
        The gates MD 4 places before plan issue, on the screen they block. Cl. 4.2.4 is
        answerable here and nowhere earlier — it attests to the ICT this plan names — and the
        two Step 2 gates stay listed because plan issue is assessed against all three. Once
        the plan is issued the card is not redrawn: the same gates, plus mutual agreement,
        are on pre-audit readiness, and two live copies of one control on one flow drift.
      */}
      {draft && ict ? (
        <IctGatesCard
          standing={ict}
          step="plan_issue"
          only={PLAN_ISSUE_GATES}
          onRecord={(gate, ok) => void recordGate(gate, ok)}
        />
      ) : null}

      {draft ? (
        <div className="row" style={{ justifyContent: "flex-end", gap: "10px" }}>
          <button type="button" className="btn sec" onClick={() => onStep("determination")}>
            Save and exit
          </button>
          <button type="button" className="btn" onClick={() => setIssuing(true)}>
            Approve plan and issue access link
          </button>
        </div>
      ) : null}

      {editing ? (
        <EditDialog
          target={editing}
          onCancel={() => setEditing(null)}
          onSave={(value) => void save(editing.field, value)}
        />
      ) : null}

      {issuing ? (
        <IssueDialog
          plan={plan}
          onCancel={() => setIssuing(false)}
          onConfirm={() => void issue()}
        />
      ) : null}
    </>
  );
}

interface EditTarget {
  field: string;
  label: string;
  value: string;
  generated: string;
}

function NarrativeField({
  label,
  value,
  generated,
  edited,
  editedBy,
  editable,
  onEdit,
}: {
  field: string;
  label: string;
  value: string;
  generated: string;
  edited: boolean;
  editedBy: { edited_by: string | null; edited_at: string } | null;
  editable: boolean;
  onEdit: () => void;
}) {
  return (
    <div className={edited ? "field edited" : "field"}>
      <div className="fieldhead">
        <label className="f">
          {label}{" "}
          {edited ? <span className="edited-tag">Manually adjusted</span> : null}
        </label>
        {editable ? (
          <button type="button" className="btn sec sm" onClick={onEdit}>
            Edit
          </button>
        ) : null}
      </div>
      <div style={{ fontSize: "13px", lineHeight: 1.65, marginTop: "5px" }}>{value}</div>
      {edited ? (
        // The Complao draft, kept beside the amendment. This is the plan's half of the
        // AI-versus-human provenance `findings.origin` carries for findings — the record
        // of what was proposed and what the human changed.
        <div className="sub" style={{ fontSize: "11px", marginTop: "5px" }}>
          Complao draft: &ldquo;{generated}&rdquo; — {editedBy?.edited_by ?? "—"} ·{" "}
          {stamp(editedBy?.edited_at)}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The agenda, day by day.
 *
 * The footer reconciles rather than reporting a shortfall: the scheduled hours equal the
 * agreed duration because the derivation guarantees it, and printing them side by side is
 * how a reader sees that the plan is the determination rather than a document beside it.
 */
function Agenda({
  plan,
  edited,
  editable,
  onEdit,
}: {
  plan: Plan;
  edited: boolean;
  editable: boolean;
  onEdit: () => void;
}) {
  return (
    <div className="card">
      <div
        className="fieldhead"
        style={{
          paddingBottom: "9px",
          borderBottom: "1px solid var(--hairline)",
          marginBottom: "14px",
        }}
      >
        <h2 style={{ border: "none", padding: 0, margin: 0 }}>Agenda</h2>
        {editable ? (
          <button type="button" className="btn sec sm" onClick={onEdit}>
            Edit note
          </button>
        ) : null}
      </div>

      <div className="tscroll">
        <table>
          <thead>
            <tr>
              <th>Time</th>
              <th>Activity</th>
              <th>Hours</th>
              <th>Method</th>
            </tr>
          </thead>
          <tbody>
            {plan.days.map((day) => (
              <Day key={day.number} day={day} />
            ))}
            <tr>
              <td />
              <td>
                <b>Scheduled audit time</b>
              </td>
              <td className="mono">
                <b>{plan.scheduled_hours}</b>
              </td>
              <td className="sub">
                Agreed duration {plan.agreed_stage1_hours} h · reconciles exactly
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="sub" style={{ marginTop: "12px" }}>
        {plan.narrative.agenda_note}
        {edited ? <span className="edited-tag"> · Manually adjusted</span> : null}
      </p>
      <p className="sub" style={{ fontSize: "11.5px" }}>
        Breaks of {plan.break_hours} h fall after every {plan.break_per_session_hours} h of
        counted time and are not part of the audit duration — {plan.break_clause}. An audit
        day holds {plan.audit_day_hours} counted hours; the break sits outside them, so a
        full day runs past {plan.audit_day_hours} hours on the clock.
      </p>
      <p className="sub" style={{ fontSize: "11.5px" }}>
        Only day one carries a date. The intake records one audit date and nothing published
        says which calendar days follow, so the remaining dates are agreed with the client
        rather than assumed here.
      </p>
    </div>
  );
}

function Day({ day }: { day: PlanDay }) {
  return (
    <>
      <tr>
        <td
          colSpan={4}
          style={{
            background: "var(--deep-forest)",
            color: "var(--amber-wash)",
            fontSize: "10px",
            letterSpacing: ".09em",
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          Day {day.number}
          {day.day_date ? ` · ${day.day_date}` : ""} · {day.starts_at}–{day.ends_at} ·{" "}
          {day.counted_hours} counted hours
        </td>
      </tr>
      {day.rows.map((row, index) => (
        <tr key={`${row.key}-${index}`} style={row.counted ? undefined : { opacity: 0.6 }}>
          <td className="mono" style={{ whiteSpace: "nowrap" }}>
            {row.starts_at} – {row.ends_at}
          </td>
          <td>
            {row.counted ? (
              <b>
                {row.kind === "clause" ? `Cl. ${row.key} — ` : ""}
                {row.title}
              </b>
            ) : (
              <span className="sub">{row.title}</span>
            )}
            {row.sub_range ? (
              <>
                <br />
                <span className="sub">
                  {row.sub_range} · {row.checkpoints} checkpoint
                  {row.checkpoints === 1 ? "" : "s"}
                </span>
              </>
            ) : null}
          </td>
          <td className="mono">{row.counted ? row.hours : "—"}</td>
          <td className="sub">
            {row.method}
            {row.remote_blocked ? (
              <>
                <br />
                <span className="pill p-red">Cannot be signed off remotely</span>
              </>
            ) : null}
          </td>
        </tr>
      ))}
    </>
  );
}

/**
 * The requested documents, derived from the standards in scope.
 *
 * A 45001-only audit never shows a quality policy slot, and a 9001-only audit never shows
 * an OH&S one. The prototype's list is authored and shows it — its ISO 45001 audit requests
 * eleven documents and none of them is the hazard identification register.
 */
function Documents({ plan }: { plan: Plan }) {
  return (
    <div className="card">
      <h2>Documents requested from the auditee</h2>
      <p className="sub" style={{ marginBottom: "12px" }}>
        Derived from the seeded checklist for the standards in scope. The auditee sees this
        as a checklist; review results are withheld from them until the session opens.
      </p>
      {plan.documents.map((document) => (
        <div className="docrow" key={document.item_id}>
          <span className="mono" style={{ width: "62px", flex: "none" }}>
            Cl. {document.clause}
          </span>
          <div className="g">
            {document.name}
            {document.standard_specific ? (
              <span className="sub"> ({document.standards.join(", ")})</span>
            ) : null}
            <small>{document.stage1_question}</small>
          </div>
        </div>
      ))}
    </div>
  );
}

function EditDialog({
  target,
  onCancel,
  onSave,
}: {
  target: EditTarget;
  onCancel: () => void;
  onSave: (value: string) => void;
}) {
  const [value, setValue] = useState(target.value);
  const unchanged = value.trim() === target.value.trim();

  return (
    <Scrim onDismiss={onCancel}>
      <h2>Edit — {target.label}</h2>
      <p className="sub" style={{ marginBottom: "14px" }}>
        The original Complao draft is retained alongside your version and the field is
        flagged as manually adjusted.
      </p>

      <div className="field">
        <label className="f">Complao draft</label>
        <textarea
          className="t"
          readOnly
          value={target.generated}
          style={{ background: "var(--surface-disabled)" }}
        />
      </div>

      <TextAreaField
        id="plan-edit"
        label="Your version"
        value={value}
        onChange={setValue}
        rows={5}
        required
        error={unchanged ? "Make a change or cancel." : undefined}
      />

      <div className="mfoot">
        <button type="button" className="btn sec" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn"
          disabled={unchanged || !value.trim()}
          onClick={() => onSave(value)}
        >
          Save edit
        </button>
      </div>
    </Scrim>
  );
}

/**
 * Screen 42 — one auditee link, and the auditor gets a permission rather than a link.
 *
 * The lead auditor already reaches this audit through their own signed-in account, which is
 * what makes every action attributable to them personally and their sign-off defensible.
 * There is nothing to issue them.
 */
function IssueDialog({
  plan,
  onCancel,
  onConfirm,
}: {
  plan: Plan;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const adjusted = new Set((plan.edits ?? []).map((entry) => entry.field));

  return (
    <Scrim onDismiss={onCancel}>
      <h2>Approve plan and issue access link</h2>
      <p className="sub" style={{ marginBottom: "14px" }}>
        This issues the approved plan to the auditee together with their access link, and
        activates your oversight access. Steps 4 and 5 begin.
      </p>

      <div className="field">
        <label className="f">Auditee access link</label>
        <p className="sub">
          Issued to the audit&rsquo;s named contact and emailed there. Single audit; expires
          on session close, and survives pauses. Readable on Steps 4 and 5 until then.
        </p>
      </div>

      <div className="field">
        <label className="f">Your oversight access</label>
        <Callout tone="info" title="Active from now.">
          Documents, session progress and the live conversation, through your own signed-in
          account — no separate link to hold. Access lasts while you are the allocated lead
          auditor on this audit.
        </Callout>
      </div>

      {adjusted.size ? (
        <Callout tone="warn" title={`${adjusted.size} field(s) manually adjusted.`}>
          Flagged in the issued plan; the Complao draft is retained in the audit file.
        </Callout>
      ) : null}

      <div className="mfoot">
        <button type="button" className="btn sec" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn" onClick={onConfirm}>
          Approve and issue
        </button>
      </div>
    </Scrim>
  );
}

function Scrim({
  children,
  onDismiss,
}: {
  children: React.ReactNode;
  onDismiss: () => void;
}) {
  return (
    <div
      className="scrim"
      onClick={(event) => {
        if (event.target === event.currentTarget) onDismiss();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true">
        {children}
      </div>
    </div>
  );
}

function stamp(value: string | null | undefined): string {
  return value ? value.slice(0, 16).replace("T", " ") : "—";
}

function problem(cause: unknown, subject: string): string {
  return cause instanceof ApiError ? cause.message : `${subject} could not be read.`;
}
